<template>
    <div class="course-whiteboard-slide video_block">
        <div class="embed-responsive embed-responsive-16by9">
            <iframe
                v-if="isYoutube"
                :id="playerId"
                ref="youtubePlayer"
                class="embed-responsive-item"
                :src="videoUrl"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowfullscreen
                title="Course video"
            />
            <video
                v-else
                class="embed-responsive-item"
                controls
                playsinline
                preload="metadata"
                :src="videoUrl"
                @play="handleNativePlay"
            />
        </div>
    </div>
</template>

<script>
import CourseBlockMixin from '@/mixins/CourseBlockMixin';

let youtubeIframeApiPromise;

function ensureYouTubeIframeApi() {
    if (window.YT && window.YT.Player) {
        return Promise.resolve(window.YT);
    }

    if (youtubeIframeApiPromise) {
        return youtubeIframeApiPromise;
    }

    youtubeIframeApiPromise = new Promise((resolve, reject) => {
        const previousReady = window.onYouTubeIframeAPIReady;

        window.onYouTubeIframeAPIReady = () => {
            if (typeof previousReady === 'function') {
                previousReady();
            }
            resolve(window.YT);
        };

        const existingScript = document.querySelector('script[src="https://www.youtube.com/iframe_api"]');
        if (existingScript) {
            existingScript.addEventListener('error', reject, { once: true });
            return;
        }

        const script = document.createElement('script');
        script.src = 'https://www.youtube.com/iframe_api';
        script.async = true;
        script.onerror = reject;
        document.head.appendChild(script);
    });

    return youtubeIframeApiPromise;
}

export default {
    name: 'VideoBlock',
    components: {},
    mixins: [
        CourseBlockMixin,
    ],

    props: {
        id: {
            type: Number,
            default: null,
        },
        module: {
            type: Number,
            default: null,
        },
        lesson: {
            type: Number,
            default: null,
        },
    },
    data() {
        return {
            hasInteracted: !!this.blockData.viewed,
            player: null,
        };
    },

    computed: {
        isYoutube() {
            return /youtu/i.test(this.blockData.videoUrl);
        },
        playerId() {
            return `course-video-${this.module}-${this.lesson}-${this.id}`;
        },
        youtubeId() {
            const videoUrl = this.blockData.videoUrl || '';
            const match = videoUrl.match(/(?:youtube\.com\/.*[?&]v=|youtu\.be\/|youtube\.com\/embed\/)([^?&/]+)/i);
            return match ? match[1] : '';
        },
        videoUrl() {
            const videoUrl = this.blockData.videoUrl || '';
            if (this.isYoutube && this.youtubeId) {
                return `https://www.youtube.com/embed/${this.youtubeId}?enablejsapi=1&playsinline=1&rel=0`;
            }
            return videoUrl;
        },
    },
    async mounted() {
        if (!this.isYoutube || this.hasInteracted || !this.youtubeId) {
            return;
        }

        try {
            await ensureYouTubeIframeApi();
            this.player = new window.YT.Player(this.playerId, {
                events: {
                    onStateChange: this.onYoutubeStateChange,
                },
            });
        } catch (error) {
            // If the YouTube API fails to load, fall back to the normal embed without interaction tracking.
        }
    },
    beforeDestroy() {
        if (this.player && typeof this.player.destroy === 'function') {
            this.player.destroy();
        }
    },
    methods: {
        markInteracted() {
            if (this.hasInteracted) {
                return;
            }
            this.hasInteracted = true;
            this.$emit('interacted', this.id);
        },
        handleNativePlay() {
            this.markInteracted();
        },
        onYoutubeStateChange(event) {
            if (window.YT && event.data === window.YT.PlayerState.PLAYING) {
                this.markInteracted();
            }
        },
    },
};
</script>
